import { createIdentificationSchema } from '../identification';
import { createFormASchema } from '../types/formA.types';
import { createFormKSchema } from '../types/formK.types';
import { KycFormData, ValidationState } from '../types/kycFormTabs.types';

/**
 * Check if a form is complete using schema validation.
 * Returns true if the form data passes all validation rules.
 */
export function isFormComplete(
  formType: string,
  data: KycFormData,
  tFormsFunc: (key: string) => string
): boolean {
  if (!data) {
    return false;
  }

  try {
    switch (formType) {
      case 'identification': {
        if (!data.identification) {
          return false;
        }
        const schema = createIdentificationSchema(tFormsFunc);
        schema.parse(data.identification);
        return true;
      }

      case 'form-k': {
        if (!data.formK) {
          return false;
        }
        const schema = createFormKSchema(tFormsFunc);
        schema.parse(data.formK);
        return true;
      }

      case 'form-a': {
        if (!data.formA) {
          return false;
        }
        const schema = createFormASchema(tFormsFunc);
        schema.parse(data.formA);
        return true;
      }

      default:
        // Forms without specific requirements are considered complete
        return true;
    }
  } catch (error) {
    // Schema validation failed - form is incomplete
    console.debug(`Form ${formType} validation failed:`, error);
    return false;
  }
}

/**
 * Compute validation state based on form completion status.
 */
export function getComputedValidationState(
  formType: string,
  formData: KycFormData,
  tFormsFunc: (key: string) => string
): ValidationState {
  if (!formData) {
    return 'pending';
  }

  const isComplete = isFormComplete(formType, formData, tFormsFunc);
  return isComplete ? 'valid' : 'invalid';
}

/**
 * Get effective validation state, preferring prop value over computed state.
 */
export function getEffectiveValidationState(
  propState: ValidationState,
  formType: string,
  formData: KycFormData,
  tFormsFunc: (key: string) => string
): ValidationState {
  // Use prop value if it's not pending
  if (propState !== 'pending') {
    return propState;
  }
  // Otherwise compute based on form data
  return getComputedValidationState(formType, formData, tFormsFunc);
}

/**
 * Calculate required form IDs based on current form data.
 */
export function getRequiredFormIds(
  identification: KycFormData['identification'],
  formK: KycFormData['formK'],
  isFormKRequired: (identification: KycFormData['identification']) => boolean,
  isFormARequired: (
    identification: KycFormData['identification'],
    formK: KycFormData['formK']
  ) => boolean
): string[] {
  const ids = ['identification'];

  if (isFormKRequired(identification)) {
    ids.push('form-k');
  }

  if (isFormARequired(identification, formK)) {
    ids.push('form-a');
  }

  return ids;
}

/**
 * Check if all required forms are complete and ready for submission.
 */
export function areAllFormsComplete(
  requiredFormIds: string[],
  formData: KycFormData,
  communicationLanguage: string,
  tFormsFunc: (key: string) => string
): boolean {
  const formsComplete = requiredFormIds.every(formId =>
    isFormComplete(formId, formData, tFormsFunc)
  );

  return formsComplete && !!communicationLanguage;
}
